import * as THREE from 'three';
import { canvasTexture, makeCanvas, panel, type LandmarkDef } from './common';
import { landmarkModel, windowGlass } from './model';

/**
 * landmarks/dakar.ts — Dakar's landmarks, all hand-built low-poly Blender models
 * (glTF). The rally arch is a drive-through ('span') scaled by width.
 */

/** The rally gantry's city board — bold DAKAR across the header band (M97).
 *  The M43-era GLB carried a text node ('TPATA') exported lying flat (edge-on =
 *  invisible from the road) and naming the wrong city; the GS7 gantry rebuild
 *  drops it entirely, and these canvas boards front BOTH faces so the header
 *  reads coming and going. */
function dakarBoard(): THREE.Mesh {
  const [c, x] = makeCanvas(512, 96);
  x.fillStyle = '#15181f';
  x.fillRect(0, 0, 512, 96);
  x.fillStyle = '#f2c41c';
  x.fillRect(0, 0, 512, 7);
  x.fillRect(0, 89, 512, 7);
  x.font = '900 62px "Arial Black", Arial, sans-serif';
  x.textAlign = 'center';
  x.textBaseline = 'middle';
  x.fillStyle = '#f4f1e8';
  x.fillText('D A K A R', 256, 50);
  const tex = canvasTexture(c);
  tex.anisotropy = 4;
  return panel(30, 5.6, tex, 0, 0, 0);
}

export const DAKAR_LANDMARKS: Record<string, LandmarkDef> = {
  // GS6: harsh-daylight city — MQ_win is the minaret's vertical strips (QA:
  // "fluorescent tubes in daylight") and Gorée's 'win' slabs were midday
  // light boxes; windowGlass reads them as dark recessed openings with a
  // whisper of warmth, the honest daylight look.
  grandeMosquee: { build: () => landmarkModel('models/landmarks/grande_mosquee.glb', { height: 44, ...windowGlass('MQ_win', 0.5) }), placement: 'side', offset: 14 },
  goreeHouse: { build: () => landmarkModel('models/landmarks/goree_house.glb', { height: 14, ...windowGlass('win', 0.4) }), placement: 'side', offset: 10 },
  renaissanceMonument: { build: () => landmarkModel('models/landmarks/renaissance_monument.glb', { height: 30 }), placement: 'side', offset: 46 },
  // GS7: the M13 procedural baobab was structurally broken (crown severed above
  // a basket-ribbed trunk). Replaced by a generated hi-res model, decimated to
  // 5.8k tris and stripped to ONE baked albedo — `baked_baobab` deliberately
  // misses every texturing.ts role regex, and skipTexturing makes that a
  // contract rather than a coincidence (see LandmarkModelOpts.skipTexturing).
  giantBaobab: { build: () => landmarkModel('models/landmarks/giant_baobab.glb', { height: 18, skipTexturing: true }), placement: 'side', offset: 9 },
  rallyArch: {
    build: (hw) =>
      landmarkModel('models/landmarks/rally_arch.glb', {
        width: 2 * hw + 18,
        // M157: the finish GOAL stands inside the arch now, and the GS7 flag
        // line (a sagging cable + square flags) hung down through the goal
        // mouth, in front of the roof net (measured in game). The goal is the
        // finish moment here; the bunting goes.
        hideMats: ['flag_green', 'flag_yellow', 'flag_red', 'gantry_cable'],
        decorate: (model) => {
          // Native fascia plate: y 13.9..20.6, faces at z ±2.30 — the boards
          // (30 x 5.6 at y 17.2 => y 14.4..20.0) sit just proud of it on both
          // sides, and the GS7 truss/flag line are kept clear of that window
          // by construction (decorate children inherit the model scale).
          const front = dakarBoard();
          front.position.set(0.5, 17.2, 2.75);
          const back = dakarBoard();
          back.position.set(0.5, 17.2, -2.75);
          back.rotation.y = Math.PI;
          model.add(front, back);
        },
      }),
    placement: 'span',
  },
};
